import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { capitalize } from '@material-ui/core';
import { potentialClientsService } from '../services/potentialClientsService';
import { membershipService } from '../services/membershipService';
import authService from '../services/authService';
import { salesRoles } from '../assets/helpers/roles';
import { notify } from '../assets/helpers/toast';

const itemsPerpage = [10, 25, 50, 100, 500];

const INITIAL_TABLE_STATE = {
  limit: itemsPerpage[2],
  offset: 0,
  data: [],
  count: 0,
  page: 1,
  isLoading: false,
  searchParam: '',
  type: 'Particular',
};

export const usePotentialClients = ({ fixedType } = {}) => {
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [membershipsDatatable, setMembershipsDatatable] = useState({
    ...INITIAL_TABLE_STATE,
    type: fixedType || INITIAL_TABLE_STATE.type,
  });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [channels, setChannels] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [serviceTypesOptions, setServiceTypesOptions] = useState([]);
  const [campaignActionOptions, setCampaignActionOptions] = useState([]);
  const [affiliatedOptions, setAffiliatedOptions] = useState([]);
  const [converteRate, setConverteRate] = useState(null);

  const channelListOptions = (arr = []) => {
    return [...(arr || [])]
      .sort((a, b) => (a?.channel < b?.channel ? -1 : a?.channel > b?.channel ? 1 : 0))
      .map((item) => ({ _id: item?._id, name: capitalize(item?.name || '') }));
  };

  const serviceTypeListOptions = (arr = []) => {
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((item) => item && typeof item === 'object' && (item._id || item.name))
      .map((item) => {
        const raw = item?.name || item?._id || '';
        return { _id: item?._id || raw, name: capitalize(String(raw).trim().toLowerCase()) };
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
  };

  const setFirstPage = () =>
    setMembershipsDatatable((prev) => ({ ...prev, page: 1, offset: 0 }));

  const handleSinceDateChange = (e) => {
    if (e > dateTo && dateTo) return notify('Ingrese una fecha en un rango válido', 'info');
    setFirstPage();
    setDateFrom(e);
  };

  const handleUntilDateChange = (e) => {
    if (e < dateFrom && dateFrom) return notify('Ingrese una fecha en un rango válido', 'info');
    setFirstPage();
    setDateTo(e);
  };

  const selectType = (type) => {
    if (fixedType) return;
    const map = { Eventos: 'Evento', Particulares: 'Particular', 'Plan free': 'Free' };
    const mapped = map[type] || type;
    if (mapped === membershipsDatatable.type) return;
    setMembershipsDatatable((prev) => ({ ...prev, page: 1, offset: 0, data: [], count: 0, type: mapped }));
  };

  const matchTypeToLabel = (type) =>
    ({ Evento: 'Eventos', Particular: 'Particulares', Free: 'Plan free' }[type] || type);

  const selectitemsPerpage = (e) =>
    setMembershipsDatatable((prev) => ({ ...prev, page: 1, offset: 0, limit: parseInt(e.target.value) }));

  const handlePageChange = (_, value) =>
    setMembershipsDatatable((prev) => ({ ...prev, page: value, offset: (value - 1) * prev.limit }));

  const handleSearchChange = (e) =>
    setMembershipsDatatable((prev) => ({ ...prev, searchParam: e?.target?.value, page: 1, offset: 0, data: false }));

  const handleChannelOptions = useCallback((arr) => setChannelOptions(arr), []);
  const handleServiceTypeOptions = useCallback((arr) => setServiceTypesOptions(arr), []);
  const handleCampaignActionOptions = useCallback((arr) => setCampaignActionOptions(arr), []);
  const handleAffiliatedOptions = useCallback((arr) => setAffiliatedOptions(arr), []);

  const clearFilters = () => {
    setChannelOptions([]);
    setCampaignActionOptions([]);
    setAffiliatedOptions([]);
    setServiceTypesOptions([]);
  };

  const validateCounterType = useCallback(() => {
    const user = authService?.getUser();
    if (user?.roles[0]?.code === salesRoles?.SALES_COUNTER && user?.type === 'Externo') {
      history.push('/dashboard/analytics');
    }
  }, [history]);

  const buildRequestBody = useCallback(() => ({
    type: membershipsDatatable.type,
    limit: membershipsDatatable.limit,
    ...(membershipsDatatable.offset && { offset: membershipsDatatable.offset }),
    ...(membershipsDatatable.searchParam && { search: membershipsDatatable.searchParam }),
    ...(dateFrom && { dateFrom: moment(dateFrom).format('YYYY-MM-DD') }),
    ...(dateTo && { dateTo: moment(dateTo).format('YYYY-MM-DD') }),
    ...(channelOptions.length > 0 && { channels: channelOptions }),
    ...(serviceTypesOptions.length > 0 && { serviceType: serviceTypesOptions }),
    ...(campaignActionOptions.length > 0 && { campaignActions: campaignActionOptions }),
    ...(affiliatedOptions.length === 1 && { isAffiliated: affiliatedOptions[0] }),
  }), [membershipsDatatable, dateFrom, dateTo, channelOptions, serviceTypesOptions, campaignActionOptions, affiliatedOptions]);

  const getMemberships = useCallback(() => {
    const body = buildRequestBody();
    setIsLoading(true);

    potentialClientsService.getPotentialClients(body)
      .then((response) => {
        if (response.status === 200) {
          const shouldLoadFilters = membershipsDatatable.offset === 0 || channels.length === 0;
          if (shouldLoadFilters) {
            const opts = response.data?.filtersOptions || {};
            setChannels(channelListOptions(opts.channels || []));
            setServiceTypes(serviceTypeListOptions(opts.serviceTypes || []));
          }
          console.log('response.data?.convertRate', response.data?.convertRate);
          setConverteRate(response.data?.convertRate);
          setMembershipsDatatable((prev) => ({
            ...prev,
            data: response.data?.data,
            count: response.data?.total,
            isLoading: false,
          }));
        }
      })
      .finally(() => setIsLoading(false));
  }, [buildRequestBody, membershipsDatatable.offset, channels.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportMemberships = () => {
    setIsLoading(true);
    potentialClientsService
      .getPotentialClients({ ...buildRequestBody(), byMail: true })
      .then((res) => {
        if (res.status === 200) notify('El archivo se está generando y será enviado a su correo', 'info');
        else notify(`${res.message}`, 'error');
      })
      .finally(() => setIsLoading(false));
  };

  const updateClientFromGrid = (data) => {
    const body = {};
    if (data?.observation) body.observation = data.observation;
    if (data?.campaignAction !== undefined) body.campaignAction = data.campaignAction;
    if (!body.observation && body.campaignAction === undefined) return;

    potentialClientsService.updatePotentialClients(data.id, body)
      .then((res) => {
        if ([200, 201].includes(res.status)) notify('Actualización exitosa', 'info');
        else notify(`${res.message}`, 'error');
      })
      .catch(() => notify('Error al actualizar', 'error'));
  };

  const renewMembership = (membership) => {
    if (isLoading) return;
    setIsLoading(true);
    membershipService.reativateMembership(membership?._id)
      .then((res) => {
        if ([200, 201].includes(res?.status)) {
          notify('Membresía reactivada', 'success');
          history.push(`/afiliations/edit-order/${membership?._id}`);
        } else notify(`${res.message}`, 'error');
      })
      .finally(() => setIsLoading(false));
  };

  const redirectToEditOrder = (orderId) => history.push(`/afiliations/edit-order/${orderId}`);
  const redirectToOrderDetail = (order) =>
    history.push(`/afiliations/order-details/${order?._id}/vehicle/${order?.vehicleId}`);

  useEffect(() => {
    getMemberships();
    validateCounterType();
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    membershipsDatatable.page,
    membershipsDatatable.offset,
    membershipsDatatable.limit,
    membershipsDatatable.type,
    dateFrom,
    dateTo,
    channelOptions,
    serviceTypesOptions,
    campaignActionOptions,
    affiliatedOptions,
  ]);

  const userType = authService?.getUserType();
  const canUploadAndExport = ![
    salesRoles.SALES_HUNTER, salesRoles.SALES_COUNTER,
    salesRoles.SALES_ADMIN_AUX, salesRoles.SALES_KEY_ACCOUNTS,
  ].includes(userType);
  const canSeeDates = ![salesRoles.SALES_ADMIN_AUX].includes(userType);
  const canSeePerPage = ![
    salesRoles.SALES_FARMER, salesRoles.SALES_HUNTER, salesRoles.SALES_COUNTER,
    salesRoles.SALES_ADMIN_AUX, salesRoles.SALES_KEY_ACCOUNTS,
  ].includes(userType);

  return {
    isLoading,
    converteRate,
    membershipsDatatable,
    dateFrom,
    dateTo,
    channels,
    serviceTypes,
    channelOptions,
    serviceTypesOptions,
    campaignActionOptions,
    affiliatedOptions,
    canUploadAndExport,
    canSeeDates,
    canSeePerPage,
    itemsPerpage,
    handleSinceDateChange,
    handleUntilDateChange,
    selectType,
    matchTypeToLabel,
    selectitemsPerpage,
    handlePageChange,
    handleSearchChange,
    clearFilters,
    handleChannelOptions,
    handleServiceTypeOptions,
    handleCampaignActionOptions,
    handleAffiliatedOptions,
    getMemberships,
    exportMemberships,
    updateClientFromGrid,
    renewMembership,
    redirectToEditOrder,
    redirectToOrderDetail,
  };
};
